import useSWR, {mutate} from 'swr'
import { useContext, useEffect, useRef } from 'react';
import { get, post } from 'aws-amplify/api';
import mutateClearCache from './mutateclearcache.js';
import UserProfileContext from '../context/userprofilecontext';

// ── Core context ──

async function getCurrentUserContext() {
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    console.log('User Context Response:', json);
    return json;
}

export function useCurrentUserContext() {
    const options = {
        refreshInterval: 10 * 1000, // Refresh every 10 seconds to keep context up-to-date
        revalidateOnFocus: true, // Revalidate when the window regains focus
    }
    const result = useSWR('CURRENTUSER_CONTEXT', ([path]) => getCurrentUserContext(), options);

    // Mount-time guard for the cross-user UI leak (backlog 2026-05-08
    // URGENT). signOutWithCleanup handles the explicit sign-out path; this
    // belt-and-suspenders catches the unusual paths — token-rotation
    // auto-switch, multi-tab where one tab signed out and another resumed,
    // or any flow that lands a different cognito user-id without invoking
    // our sign-out helper. When the resolved user-id differs from the last
    // one we saw, purge the rest of the SWR cache (everything except this
    // hook's own key — that just loaded with the new user's data).
    const lastUserIdRef = useRef(null);
    const currentUserID = result.data?.user?.userID || null;
    useEffect(() => {
        if (!currentUserID) {
            // No user yet (initial load, signed out, etc.) — record null
            // so the first real user-id triggers the user-change path.
            lastUserIdRef.current = null;
            return;
        }
        if (lastUserIdRef.current && lastUserIdRef.current !== currentUserID) {
            // User changed without going through signOutWithCleanup — purge.
            mutate(
                key => key !== 'CURRENTUSER_CONTEXT',
                undefined,
                { revalidate: false },
            );
        }
        lastUserIdRef.current = currentUserID;
    }, [currentUserID]);

    return result
}

// ── Available customers (replaces available_orgs) ──

export async function getCustomersForCurrentUser() {
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context/available_customers',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    return json;
}

export function useCustomersForCurrentUser(shouldFetch = true) {
    const result = useSWR(shouldFetch ? 'CUSTOMERS_FOR_CURRENT_USER' : null, ([path]) => getCustomersForCurrentUser());
    return result
}

// ── Switch active customer ──

export async function mutateCurrentUserCustomer(customerID) {
    const update_function = async () => {
        const payload = {
            apiName: 'UserContextApi',
            path: 'user_context/current_customer',
            options: {
                body: {
                    customerID: customerID,
                }
            }
        };
        const restOperation = post(payload);
        const { body } = await restOperation.response;
        let json = await body.json();
        return json;
    }
    // Await the customer switch BEFORE clearing downstream caches.
    // Otherwise revalidations (e.g. CASES_FOR_CURRENT_CUSTOMER) can race
    // ahead of the backend session change and refill the cache with cases
    // belonging to the previous customer.
    await mutate('CURRENTUSER_CONTEXT', update_function, { revalidate: true, populateCache: true });
    await mutateClearCache();
}

// ── Available cases for current customer ──

async function getCasesForCurrentCustomer() {
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context/current_customer/available_cases',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    return json;
}

export function useCasesForCurrentCustomer(shouldFetch = true) {
    // Partition the SWR cache by customerID so that switching customers
    // does not surface stale cases from a previous customer. When the
    // current customer changes, the key changes and SWR fetches fresh.
    const { userProfile } = useContext(UserProfileContext);
    const customerID = userProfile?.currentCustomer?.customerID || null;
    const result = useSWR(
        shouldFetch && customerID ? ['CASES_FOR_CURRENT_CUSTOMER', customerID] : null,
        () => getCasesForCurrentCustomer()
    );
    return result;
}

// ── Switch active case ──

export async function mutateCurrentUserCase(caseID) {
    const update_function = async () => {
        const payload = {
            apiName: 'UserContextApi',
            path: 'user_context/current_customer/current_case',
            options: {
                body: {
                    caseID: caseID,
                }
            }
        };
        const restOperation = post(payload);
        const { body } = await restOperation.response;
        let json = await body.json();
        return json;
    }
    // Await the case switch BEFORE clearing downstream caches so that
    // case-scoped revalidations don't race the backend session change.
    await mutate('CURRENTUSER_CONTEXT', update_function, { revalidate: true, populateCache: true });
    await mutateClearCache();
}

// ── Super admin check ──

export async function getIsSuperAdmin() {
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context/is_super_admin',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    return json.isSuperAdmin === "true";
}

export function useIsSuperAdmin(shouldFetch = true) {
    const result = useSWR(shouldFetch ? 'IS_SUPER_ADMIN' : null, ([path]) => getIsSuperAdmin());
    return result;
}

// ══════════════════════════════════════════════════════════════════════
// DEPRECATED — preserved for backward compatibility during transition
// Remove these once all frontend code is migrated to Customer-based hooks
// ══════════════════════════════════════════════════════════════════════

export async function getOrgsForCurrentUser() {
    console.warn('getOrgsForCurrentUser is deprecated, use getCustomersForCurrentUser');
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context/available_orgs',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    return json;
}

export function useOrgsForCurrentUser(shouldFetch = true) {
    console.warn('useOrgsForCurrentUser is deprecated, use useCustomersForCurrentUser');
    const result = useSWR(shouldFetch ? 'ORGS_FOR_CURRENT_USER' : null, ([path]) => getOrgsForCurrentUser());
    return result
}

export function mutateCurrentUserOrg(orgID) {
    console.warn('mutateCurrentUserOrg is deprecated, use mutateCurrentUserCustomer');
    const update_function = async () => {
        const payload = {
            apiName: 'UserContextApi',
            path: 'user_context/current_org',
            options: {
                body: {
                    orgID: orgID,
                }
            }
        };
        const restOperation = post(payload);
        const { body } = await restOperation.response;
        let json = await body.json();
        return json;
    }
    mutate('CURRENTUSER_CONTEXT', update_function, { revalidate: true, populateCache: true });
    mutateClearCache();
}

async function getCasesForCurrentUserAndOrg() {
    console.warn('getCasesForCurrentUserAndOrg is deprecated, use getCasesForCurrentCustomer');
    const payload = {
        apiName: 'UserContextApi',
        path: 'user_context/current_org/available_cases',
    };
    const restOperation = get(payload);
    const { body } = await restOperation.response;
    let json = await body.json();
    return json;
}

export function useCasesForCurrentUserAndOrg(shouldFetch = true) {
    console.warn('useCasesForCurrentUserAndOrg is deprecated, use useCasesForCurrentCustomer');
    const result = useSWR(shouldFetch ? 'CASES_FOR_CURRENT_USER_AND_ORG' : null, ([path]) => getCasesForCurrentUserAndOrg());
    return result
}